import { useContext, useEffect, useMemo, useState } from "react";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import NoData from "../../../../../images/NoData.svg";
import Close from "../../../../../images/Close.svg";
import { useLazyQuery } from "@apollo/client";
import { LicenseTypes } from "../../../../../utils/Enum.types";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import {
  AtsStckItemDetailsEdges,
  AtsStckItemDetailsOrderField,
  GetItemsDetailsResult,
  GetItemsDetailsVars,
  ItemDetailsQueryType,
} from "../../masters/items/queries/types";
import { GetItemsDetails } from "../../masters/items/queries/queries";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  emptyFunc,
} from "../../../../../utils/constants";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { TableHeaderProps } from "../../../../../utils/types";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { ColumnVisibilityFor } from "../../../../../utils/Enum.Datagrid";
import {
  getModifiedScrollHeight,
  handleMUISelectEvent,
} from "../../../../../utils/UtilFunctions";
import useLabels from "../../../../../customHooks/useLabels";
import Input from "../../../../../stories/Input/Input";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { Button } from "../../../../../stories/Button/Button";
import { Title } from "../../../../../stories/Title/Title";
import CompaniesAutocomplete from "../../../customer/companies/CompaniesAutocomplete";
import { payloadTypes } from "../../../../../context/reducer";
import SubsidiarySelect from "../../../../../customComponents/SubsidiarySelect";
import { AppContext } from "../../../../../context/context";
import SiteSelect from "../../../../../customComponents/SiteSelect";
import ItemSelect from "../../../../../customComponents/ItemSelect";
import { Keys } from "../../../../../utils/Enum.keys";
import { mapsPayloadType } from "../../maps/context/reducer";
import { MapsContext } from "../../maps/context/context";
import { InstConfigIds } from "./RaiseTicket";
import { TableType } from "../../../customer/companies/queries/types";
import LoadingModal from "../../../../pages/LoadingModal";
import ItemsAutocomplete from "../../../../../customComponents/ItemsAutocomplete";

interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  setConfigIds: React.Dispatch<React.SetStateAction<InstConfigIds>>;
  setRaiseTicketPage: React.Dispatch<React.SetStateAction<boolean>>;
  setAssetType: React.Dispatch<React.SetStateAction<TableType>>;
  configIds: InstConfigIds;
}

export interface NavTypeProps {
  NavType: LicenseTypes;
}

const FilterAssetTickets = ({
  setModalFlag,
  setConfigIds,
  setRaiseTicketPage,
  setAssetType,
  configIds,
}: Props) => {
  const { Items } = useAssetTableJson();
  const { state, dispatch } = useContext(AppContext);
  const { dispatch: MapDispatch } = useContext(MapsContext);
  const { companyLabel, subsidiaryLabel, subsidiarySiteLabel, itemLabel } =
    useLabels();

  const { customer_id } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const [items, setItems] = useState<AtsStckItemDetailsEdges[]>([]);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const [searchJunction, setSearchJunction] = useState("");

  const [GetJunctionItems, { data, loading, fetchMore }] = useLazyQuery<
    GetItemsDetailsResult,
    GetItemsDetailsVars
  >(GetItemsDetails);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id || state.companyId) {
          GetJunctionItems({
            variables: {
              customerID: customer_id ? customer_id : 0,
              companyID: state.companyId,
              subsidiaryID: state.subsidiaryId,
              siteID: state.subSiteId,
              junctionID: 0,
              input: {
                query_type: ItemDetailsQueryType.ALL,
                stckCategoryID: 0,
                stckGroupID: 0,
                stckManufacturerID: 0,
                stckUomID: 0,
                stckSubCategoryID: 0,
                itemID: configIds.itemDetailsId,
                ldgrID: 0,
                empID: user_details.id!,
              },
              after: null,
              first: ROWS_PER_PAGE,
              name: searchJunction,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemDetailsOrderField.CREATED_AT,
              },
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    user_details.id,
    GetJunctionItems,
    searchJunction,
    state.companyId,
    state.subSiteId,
    state.subsidiaryId,
    configIds.itemDetailsId,
  ]);

  const dynamicHeaders: TableHeaderProps[] = Items.List2.map((header) => ({
    headerName: header.headerName,
    field: header.field,
    headerAlign: header.headerAlign as GridAlignment,
    align: header.align as GridAlignment,
    flex: header.flex,
    hideable: header.hideable,
    cellClassName: header.cellClassName,
  }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const rows1: GridValidRowModel[] = useMemo(() => {
    return items.map(({ node: data }, index) => {
      return {
        id: index + 1,
        description: `${data.item.desc} (${data.prodSerialNo})`,
        itemDetaildId: data.id,
        currentCompany: data.currentCompany.name,
        currentSubsidiary: data.currentSubsidiary.name,
        currentSite: data.currentSite.name,
      };
    });
  }, [items]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(ColumnVisibilityFor.ITEMS_LIST);
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.ITEMS_LIST,
      JSON.stringify(newModel)
    );
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemsDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,

              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetItemsDetails.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  const handleScroll = () => {
    if (hasNextPage && !loading) {
      fetchMore({
        variables: {
          first: FETCH_MORE_DATA,
          after: endCursor,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;

          const newEdges = fetchMoreResult.GetItemsDetails.edges;
          const pageInfo = fetchMoreResult.GetItemsDetails.pageInfo;
          setEndCursor(pageInfo.endCursor);
          setHasNextPage(pageInfo.hasNextPage);

          const duplicateCheck = prevResult.GetItemsDetails.edges.filter(
            ({ node: { id } }) =>
              newEdges.findIndex(({ node: { id: newId } }) => newId === id) !==
              -1
          );

          if (duplicateCheck.length > 0) return prevResult;

          return {
            GetItemsDetails: {
              edges: [...items, ...newEdges],
              pageInfo,
              totalCount: data ? data.GetItemsDetails.totalCount! : 0,
            },
          };
        },
      });
    }
  };

  const handleClose = () => {
    setModalFlag(false);

    dispatch({
      type: payloadTypes.SET_COMPANY_ID,
      payload: {
        companyId: 0,
      },
    });
    dispatch({
      type: payloadTypes.SET_SUB_ID,
      payload: {
        subsidiaryId: 0,
      },
    });
    dispatch({
      type: payloadTypes.SET_SUB_SITE_ID,
      payload: {
        subSiteId: 0,
      },
    });
  };

  return (
    <>
      <div className="item-ob__title">
        <Title>Items List</Title>
        <img src={Close} alt="" onClick={() => handleClose()} />
      </div>

      <div className={`item-ob__modal`}>
        <div className="item-ob__select row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchJunction(e.target.value);
              }}
            />
          </div>
          <div className="col">
            <ItemsAutocomplete
              onChange={(newValue) => {
                if (newValue) {
                  setConfigIds({
                    ...configIds,
                    itemDetailsId: newValue.value,
                  });
                } else {
                  setConfigIds({
                    ...configIds,
                    itemDetailsId: 0,
                  });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === Keys.ENTER) {
                  e.preventDefault();
                  handleMUISelectEvent(e);
                }
              }}
              value={configIds.itemDetailsId}
              placeholder=""
              isComponent={false}
              label={itemLabel}
            />
          </div>
          <div className="col">
            <CompaniesAutocomplete
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              onKeyDown={emptyFunc}
              value={state.companyId ? state.companyId : 0}
              label={companyLabel}
            />
          </div>
          <div className="col">
            <SubsidiarySelect
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              onKeyDown={emptyFunc}
              companyID={state.companyId ? state.companyId : 0}
              value={state.subsidiaryId ? state.subsidiaryId : 0}
              label={subsidiaryLabel}
            />
          </div>
          <div className="col">
            <SiteSelect
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: newValue.value,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              companyId={state.companyId ? state.companyId : 0}
              subsidiaryId={state.subsidiaryId ? state.subsidiaryId : 0}
              onKeyDown={emptyFunc}
              value={state.subSiteId ? state.subSiteId : 0}
              label={subsidiarySiteLabel}
            />
          </div>
        </div>
        <div className="item-ob__tableblock">
          {items.length ? (
            <StyledDatagrid
              columns={columns}
              rows={rows1}
              rowHeight={TABLE_ROW_HEIGHT}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              initialState={{
                columns: {
                  columnVisibilityModel: columnVisibilityModel,
                },
              }}
              disableRowSelectionOnClick
              hideFooter
              onCellClick={(params) => {
                if (params.field === "description") {
                  MapDispatch({
                    type: mapsPayloadType.ASSET_TYPE_ID,
                    payload: { assetTypeId: params.row.itemDetaildId },
                  });
                  setConfigIds({
                    ...configIds,
                    itemDetailsId: params.row.itemDetaildId,
                  });

                  setRaiseTicketPage(true);
                  setAssetType(TableType.ASSET);
                  handleClose();
                }
              }}
              onRowsScrollEnd={handleScroll}
            />
          ) : (
            <>
              <img src={NoData} alt="" className="nodata-image" />
            </>
          )}
        </div>
        <div className="row g-0">
          <div className="col-2">
            <Button mode="cancel" onClick={() => setModalFlag(false)} />
          </div>
          <div className="col"></div>
          <div className="col-2">
            Total Count : <Button>{data?.GetItemsDetails.totalCount}</Button>
          </div>
        </div>
      </div>
      {/* <LoadingModal flag={loading} /> */}
    </>
  );
};

export default FilterAssetTickets;
